import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  QueryList,
  ViewChild,
  ViewChildren,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { injectRegisterIcons } from '@ngneat/svg-icon';
import { TranslatePipe } from '@ngx-translate/core';

import { TextInputComponent } from '@shared/components/text-input/text-input.component';
import { DialogRef } from '@shared/modules/dialog';

import { appSearch20Icon } from '@icons/search-20';

import { QuickNavigationService } from './quick-navigation.service';

interface QuickNavigationLabelPart {
  text: string;
  isMatch: boolean;
}

@Component({
  selector: 'app-quick-navigation-dialog',
  imports: [FormsModule, RouterLink, TextInputComponent, TranslatePipe],
  templateUrl: './quick-navigation-dialog.component.html',
  host: { class: 'flex max-h-full flex-col', '(body:keydown)': 'onKeydown($event)' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickNavigationDialogComponent {
  readonly #quickNavigationService = inject(QuickNavigationService);
  readonly #dialogRef = inject(DialogRef);

  @ViewChild('searchInput') private readonly searchInput!: TextInputComponent;
  @ViewChildren('optionElement') private readonly optionElements!: QueryList<ElementRef<HTMLElement>>;

  protected readonly search = signal('');
  protected readonly activeIndex = signal(0);
  protected readonly options = computed(() => {
    const search = this.search().trim().toLocaleLowerCase();
    const options = this.#quickNavigationService.options();
    const searchWords = search ? search.split(/\s+/) : [];

    return options
      .filter((option) => {
        const label = option.label.toLocaleLowerCase();

        return searchWords.every((searchWord) => label.includes(searchWord));
      })
      .map((option) => ({
        id: option.id,
        labelParts: this.#getLabelParts(option.label, searchWords),
        route: option.route,
      }));
  });
  protected readonly activeOptionId = computed(() => this.options()[this.activeIndex()]?.id ?? null);

  constructor() {
    injectRegisterIcons(appSearch20Icon);
  }

  public onSearchInput(search: string): void {
    this.search.set(search);
    this.activeIndex.set(0);
  }

  public onKeydown(event: KeyboardEvent): void {
    if (!this.searchInput.isInputTarget(event.target)) {
      this.searchInput.focus();
    }

    const options = this.options();

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#setActiveIndex(Math.min(this.activeIndex() + 1, options.length - 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.#setActiveIndex(Math.max(this.activeIndex() - 1, 0));
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      this.#setActiveIndex(0);
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      this.#setActiveIndex(options.length - 1);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.optionElements.get(this.activeIndex())?.nativeElement.click();
    }
  }

  public onOptionSelect(event: MouseEvent): void {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    this.onDismiss();
  }

  public onDismiss(): void {
    this.#dialogRef.dismiss();
  }

  #setActiveIndex(index: number): void {
    if (index < 0) {
      return;
    }

    this.activeIndex.set(index);
    queueMicrotask(() => this.optionElements.get(index)?.nativeElement.scrollIntoView({ block: 'nearest' }));
  }

  #getLabelParts(label: string, searchWords: string[]): QuickNavigationLabelPart[] {
    if (searchWords.length === 0) {
      return [{ text: label, isMatch: false }];
    }

    const normalisedLabel = label.toLocaleLowerCase();
    const matchRanges = searchWords.flatMap((searchWord) => {
      const ranges: { start: number; end: number }[] = [];
      let start = normalisedLabel.indexOf(searchWord);

      while (start !== -1) {
        ranges.push({ start, end: start + searchWord.length });
        start = normalisedLabel.indexOf(searchWord, start + searchWord.length);
      }

      return ranges;
    });
    const mergedRanges = matchRanges
      .sort((first, second) => first.start - second.start || first.end - second.end)
      .reduce<{ start: number; end: number }[]>((ranges, range) => {
        const previousRange = ranges.at(-1);

        if (previousRange && range.start <= previousRange.end) {
          previousRange.end = Math.max(previousRange.end, range.end);
        } else {
          ranges.push(range);
        }

        return ranges;
      }, []);
    const parts: QuickNavigationLabelPart[] = [];
    let currentIndex = 0;

    for (const range of mergedRanges) {
      if (range.start > currentIndex) {
        parts.push({ text: label.slice(currentIndex, range.start), isMatch: false });
      }

      parts.push({ text: label.slice(range.start, range.end), isMatch: true });
      currentIndex = range.end;
    }

    if (currentIndex < label.length) {
      parts.push({ text: label.slice(currentIndex), isMatch: false });
    }

    return parts;
  }
}
